import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, Phone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  const navItems = [
    { path: '/', labelPt: 'Início', labelEn: 'Home' },
    { path: '/sobre', labelPt: 'Sobre', labelEn: 'About' },
    { path: '/servicos', labelPt: 'Serviços', labelEn: 'Services' },
    { path: '/blog', labelPt: 'Blog', labelEn: 'Blog' },
    { path: '/contato', labelPt: 'Contato', labelEn: 'Contact' },
  ];

  return (
    <header className="cv-header fixed top-0 left-0 right-0 z-50" data-testid="site-header">
      <div className="cv-container">
        <div className="flex items-center justify-between h-[88px]">
          <Link to="/" className="flex items-center outline-none focus-visible:ring-2 focus-visible:ring-cobalt focus-visible:ring-offset-2 rounded-sm" data-testid="logo-link" aria-label="Costa Valente Trading — Início">
            {/* Desktop: H1 horizontal */}
            <img
              src="/logo/logo-horizontal.svg"
              alt="Costa Valente"
              data-testid="header-logo-desktop"
              className="hidden md:block h-9 w-auto"
              width="248"
              height="36"
            />
            {/* Mobile: H2 compacta */}
            <img
              src="/logo/logo-compact.svg"
              alt="Costa Valente"
              data-testid="header-logo-mobile"
              className="block md:hidden h-10 w-auto"
              width="170"
              height="40"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-link-${item.path.replace('/', '') || 'home'}`}
                  className={`relative text-[15px] font-medium tracking-wide transition-colors outline-none ${
                    isActive ? 'text-cobalt' : 'text-abyssal hover:text-cobalt'
                  }`}
                >
                  {language === 'pt' ? item.labelPt : item.labelEn}
                  {isActive && (
                    <span className="absolute -bottom-2 left-0 right-0 h-[2px] bg-cobalt rounded-full" aria-hidden="true"></span>
                  )}
                </Link>
              );
            })}

            <button
              onClick={toggleLanguage}
              data-testid="language-toggle-desktop"
              className="flex items-center gap-1.5 text-steel hover:text-cobalt transition-colors text-sm font-medium"
              aria-label="Toggle language"
            >
              <Globe className="h-4 w-4" />
              <span className="font-semibold">{language.toUpperCase()}</span>
            </button>

            <a
              href="https://wa.me/5527996482098"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-cta"
              className="inline-flex items-center gap-2 bg-abyssal hover:bg-cobalt text-white px-5 py-2.5 rounded-md text-sm font-semibold transition-colors"
            >
              <Phone className="h-4 w-4" />
              {t('Falar com especialista', 'Talk to specialist')}
            </a>
          </nav>

          <button
            className="lg:hidden text-abyssal p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Menu"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {isMenuOpen && (
          <nav className="lg:hidden py-5 space-y-1 border-t border-[#E6E6ED]" data-testid="mobile-menu">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  data-testid={`mobile-nav-link-${item.path.replace('/', '') || 'home'}`}
                  className={`block py-3 text-base font-medium ${
                    isActive ? 'text-cobalt' : 'text-abyssal'
                  }`}
                >
                  {language === 'pt' ? item.labelPt : item.labelEn}
                </Link>
              );
            })}
            <button
              onClick={() => { toggleLanguage(); setIsMenuOpen(false); }}
              data-testid="language-toggle-mobile"
              className="flex items-center gap-2 text-steel py-3 text-sm font-medium"
            >
              <Globe className="h-4 w-4" />
              <span>{language === 'pt' ? 'English' : 'Português'}</span>
            </button>
            <a
              href="https://wa.me/5527996482098"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-abyssal hover:bg-cobalt text-white px-5 py-3 rounded-md text-sm font-semibold mt-3 transition-colors"
            >
              <Phone className="h-4 w-4" />
              {t('Falar com especialista', 'Talk to specialist')}
            </a>
          </nav>
        )}
      </div>
    </header>
  );
};

export { Header };
export default Header;
